import React, { useRef, useState } from 'react';
import { Table, Modal, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReceiptItem, SalesKPISummary } from '../../model/report';
import { exportExcel } from '../../utils/util';
import { ReceiptThermalSlip80mm } from './components/thermal-slip-80mm';
import { printDocument } from '../../utils/print-utils';
import { ROW_HEIGHT_SMALL, useMeasuredRowScrollY, useTablePagination } from '../common/table-pagination';

interface Props {
    kpis: SalesKPISummary;
    receipts: ReceiptItem[];
    dailyTrend: { date: string; sales: number; bills: number }[];
    onRefresh: () => void;
}

const ReportSalesSummary: React.FC<Props> = ({ kpis, receipts, dailyTrend }) => {
    const pagination = useTablePagination();
    const { ref: receiptsRef, y: receiptsScrollY } = useMeasuredRowScrollY(420);
    const { ref: trendRef, y: trendScrollY } = useMeasuredRowScrollY(420, ROW_HEIGHT_SMALL);
    // One row per day: a year-long range rendered 365 of them.
    const dailyTrendPagination = useTablePagination();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedReceipt, setSelectedReceipt] = useState<ReceiptItem | null>(null);
    const [isSlipMode, setIsSlipMode] = useState<boolean>(false);
    const receiptPrintRef = useRef<HTMLDivElement>(null);

    // Filter receipts
    const filteredReceipts = receipts.filter(r =>
        r.bill_no.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.cashier_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.member_name && r.member_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Columns for Daily Trend
    const dailyColumns: ColumnsType<any> = [
        { title: 'วันที่', dataIndex: 'date', key: 'date', width: 140 },
        {
            title: 'ยอดขายรวม (บาท)',
            dataIndex: 'sales',
            key: 'sales',
            render: (val: number) => <span style={{ fontWeight: 700, color: 'var(--primary, #FF7A59)' }}>฿{val.toLocaleString()}</span>
        },
        { title: 'จำนวนบิล', dataIndex: 'bills', key: 'bills', render: (val: number) => `${val} บิล` },
        {
            title: 'ยอดเฉลี่ยต่อบิล',
            key: 'avg',
            render: (_: any, record: any) => `฿${Math.round(record.sales / (record.bills || 1)).toLocaleString()}`
        }
    ];

    // Columns for Receipt History Table
    const receiptColumns: ColumnsType<ReceiptItem> = [
        {
            title: 'เลขที่บิล',
            dataIndex: 'bill_no',
            key: 'bill_no',
            render: (val: string) => <span style={{ fontWeight: 700 }}>{val}</span>
        },
        { title: 'วัน-เวลา', dataIndex: 'create_date', key: 'create_date' },
        { title: 'พนักงานแคชเชียร์', dataIndex: 'cashier_name', key: 'cashier_name' },
        { title: 'สมาชิก / ลูกค้า', dataIndex: 'member_name', key: 'member_name' },
        {
            title: 'การชำระเงิน',
            dataIndex: 'payment_type_name',
            key: 'payment_type_name',
            render: (val: string, record: ReceiptItem) => {
                const typeClass = record.payment_type_id === 1 ? 'cash' : record.payment_type_id === 2 ? 'qr' : 'credit';
                return <span className={`report-badge-payment ${typeClass}`}>{val}</span>;
            }
        },
        {
            title: 'ส่วนลด',
            key: 'discount',
            render: (_: any, record: ReceiptItem) => {
                const totalDisc = record.discount + record.discount_bill;
                return totalDisc > 0 ? <span style={{ color: '#EF4444' }}>-฿{totalDisc.toLocaleString()}</span> : '-';
            }
        },
        {
            title: 'ยอดสุทธิ',
            dataIndex: 'net_price',
            key: 'net_price',
            render: (val: number) => <span style={{ fontWeight: 800, color: 'var(--primary, #FF7A59)', fontSize: '14.5px' }}>฿{val.toLocaleString()}</span>
        },
        {
            title: 'จัดการ',
            key: 'action',
            width: 190,
            render: (_: any, record: ReceiptItem) => (
                <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                        type="button"
                        className="btn-table-view"
                        onClick={() => {
                            setSelectedReceipt(record);
                            setIsSlipMode(false);
                        }}
                    >
                        <FontAwesomeIcon icon="eye" /> ดูบิล
                    </button>
                    <button
                        type="button"
                        className="btn-table-view"
                        onClick={() => {
                            setSelectedReceipt(record);
                            setIsSlipMode(true);
                        }}
                        title="พิมพ์ใบเสร็จออกเครื่องพิมพ์สลิป 80mm"
                    >
                        <FontAwesomeIcon icon="print" /> 80mm
                    </button>
                </div>
            )
        }
    ];

    const handleExportExcel = () => {
        const exportData = filteredReceipts.map(r => ({
            'เลขที่บิล': r.bill_no,
            'วัน-เวลา': r.create_date,
            'พนักงานขาย': r.cashier_name,
            'ลูกค้า': r.member_name,
            'ประเภทชำระ': r.payment_type_name,
            'ยอดรวมก่อนลด': r.total_price,
            'ส่วนลด': r.discount + r.discount_bill,
            'ภาษีมูลค่าเพิ่ม': r.vat_amount,
            'ยอดสุทธิ': r.net_price,
            'รับเงิน': r.receive,
            'เงินทอน': r.change
        }));
        exportExcel('รายงานประวัติบิลขาย', exportData);
    };

    return (
        <div>
            {/* KPI Cards Strip */}
            <div className="report-kpi-grid">
                <div className="kpi-card">
                    <div className="kpi-icon-box kpi-sales">
                        <FontAwesomeIcon icon="dollar-sign" />
                    </div>
                    <div className="kpi-info-box">
                        <span className="kpi-label">ยอดขายสุทธิรวม</span>
                        <span className="kpi-value">฿{kpis.totalSales.toLocaleString()}</span>
                        <span className="kpi-subtext">ภาษีมูลค่าเพิ่ม ฿{Math.round(kpis.totalVat).toLocaleString()}</span>
                    </div>
                </div>

                <div className="kpi-card">
                    <div className="kpi-icon-box kpi-bills">
                        <FontAwesomeIcon icon="receipt" />
                    </div>
                    <div className="kpi-info-box">
                        <span className="kpi-label">จำนวนบิลทั้งหมด</span>
                        <span className="kpi-value">{kpis.totalBills} บิล</span>
                        <span className="kpi-subtext">เฉลี่ย ฿{Math.round(kpis.avgBillAmount).toLocaleString()} / บิล</span>
                    </div>
                </div>

                <div className="kpi-card">
                    <div className="kpi-icon-box kpi-profit">
                        <FontAwesomeIcon icon="chart-line" />
                    </div>
                    <div className="kpi-info-box">
                        <span className="kpi-label">กำไรขั้นต้น</span>
                        <span className="kpi-value" style={{ color: kpis.totalProfit === null ? '#94A3B8' : '#10B981' }} data-testid="report-kpi-profit">
                            {kpis.totalProfit === null ? '-' : `฿${kpis.totalProfit.toLocaleString()}`}
                        </span>
                        <span className="kpi-subtext">
                            {kpis.totalProfit === null
                                ? 'ไม่มีรายการในบิลให้คำนวณ'
                                : kpis.totalSales > 0
                                    ? `อัตรากำไร ${((kpis.totalProfit / kpis.totalSales) * 100).toFixed(1)}% (จากต้นทุนสินค้าที่บันทึก)`
                                    : 'จากต้นทุนสินค้าที่บันทึก'}
                        </span>
                    </div>
                </div>

                <div className="kpi-card">
                    <div className="kpi-icon-box kpi-items">
                        <FontAwesomeIcon icon="box-open" />
                    </div>
                    <div className="kpi-info-box">
                        <span className="kpi-label">สินค้าที่ขายได้</span>
                        <span className="kpi-value">{kpis.totalItemsSold} ชิ้น</span>
                        <span className="kpi-subtext">ส่วนลดรวม ฿{kpis.totalDiscount.toLocaleString()}</span>
                    </div>
                </div>
            </div>

            {/* Payment Breakdown Mini Cards */}
            <div className="payment-methods-strip">
                <div className="payment-method-card">
                    <span className="payment-method-name">💵 ยอดเงินสด:</span>
                    <span className="payment-method-val">฿{kpis.cashSales.toLocaleString()}</span>
                </div>
                <div className="payment-method-card">
                    <span className="payment-method-name">📱 ยอดสแกน QR / เงินโอน:</span>
                    <span className="payment-method-val">฿{kpis.transferSales.toLocaleString()}</span>
                </div>
                <div className="payment-method-card">
                    <span className="payment-method-name">💳 ยอดบัตรเครดิต:</span>
                    <span className="payment-method-val">฿{kpis.creditSales.toLocaleString()}</span>
                </div>
            </div>

            {/* Daily Trend Table Section */}
            {dailyTrend.length > 0 && (
                <div className="report-card-section">
                    <div className="report-section-header">
                        <span className="report-section-title">
                            <FontAwesomeIcon icon="calendar-days" style={{ color: 'var(--primary, #FF7A59)' }} />
                            <span>แนวโน้มยอดขายรายวัน (Sales Trend)</span>
                        </span>
                    </div>
                    <div ref={trendRef}>
                    <Table
                        columns={dailyColumns}
                        dataSource={dailyTrend}
                        rowKey="date"
                        pagination={dailyTrendPagination}
                        scroll={{ y: trendScrollY }}
                        size="small"
                    />
                    </div>
                </div>
            )}

            {/* Detailed Receipt Log Table Section */}
            <div className="report-card-section">
                <div className="report-section-header">
                    <span className="report-section-title">
                        <FontAwesomeIcon icon="file-lines" style={{ color: 'var(--primary, #FF7A59)' }} />
                        <span>รายการประวัติบิลขาย ({filteredReceipts.length} รายการ)</span>
                    </span>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <input
                            type="text"
                            className="report-search-input"
                            placeholder="ค้นหาเลขที่บิล, แคชเชียร์, สมาชิก..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                        <button
                            type="button"
                            className="btn-report-action btn-excel"
                            onClick={handleExportExcel}
                        >
                            <FontAwesomeIcon icon="file-excel" /> ส่งออก Excel
                        </button>
                    </div>
                </div>

                <div ref={receiptsRef}>
                <Table
                    columns={receiptColumns}
                    dataSource={filteredReceipts}
                    rowKey="id"
                    pagination={pagination}
                    scroll={{ y: receiptsScrollY }}
                />
                </div>
            </div>

            {/* View Receipt Detail Modal & 80mm Slip */}
            <Modal
                title={isSlipMode ? "ตัวอย่างสลิปใบเสร็จ (80mm Thermal Slip)" : "รายละเอียดใบเสร็จรับเงิน"}
                open={selectedReceipt !== null}
                onCancel={() => setSelectedReceipt(null)}
                footer={[
                    <Button key="mode" onClick={() => setIsSlipMode(!isSlipMode)}>
                        {isSlipMode ? 'สลับเป็นรูปแบบหน้าจอ' : 'สลับเป็นรูปแบบสลิป 80mm'}
                    </Button>,
                    <Button key="close" onClick={() => setSelectedReceipt(null)}>
                        ปิด
                    </Button>,
                    <Button
                        key="print"
                        type="primary"
                        style={{ background: isSlipMode ? '#10B981' : 'var(--primary, #FF7A59)', borderColor: isSlipMode ? '#10B981' : 'var(--primary, #FF7A59)' }}
                        onClick={() => {
                            printDocument(receiptPrintRef.current, {
                                documentType: 'thermal-80mm',
                                title: `ใบเสร็จรับเงิน ${selectedReceipt?.bill_no || ''}`
                            });
                        }}
                    >
                        <FontAwesomeIcon icon="print" /> {isSlipMode ? 'สั่งพิมพ์ออกเครื่องสลิป 80mm' : 'พิมพ์ใบเสร็จ'}
                    </Button>
                ]}
                width={isSlipMode ? 380 : 520}
                centered
            >
                {selectedReceipt && (
                    <div ref={receiptPrintRef} style={{ background: '#FFFFFF' }}>
                        {isSlipMode ? (
                            <div style={{ padding: '10px 0', background: '#F8FAFC', display: 'flex', justifyContent: 'center' }}>
                                <ReceiptThermalSlip80mm receipt={selectedReceipt} />
                            </div>
                        ) : (
                            <div className="receipt-modal-content">
                                <div className="receipt-modal-header">
                                <div className="receipt-store-title">JinDa POS Coffee & Bakery</div>
                                <div style={{ fontSize: '13px', color: '#64748B' }}>ใบเสร็จรับเงินอย่างย่อ</div>
                            </div>

                            <div className="receipt-meta-grid">
                                <div><strong>เลขที่บิล:</strong> {selectedReceipt.bill_no}</div>
                                <div><strong>วันที่:</strong> {selectedReceipt.create_date}</div>
                                <div><strong>แคชเชียร์:</strong> {selectedReceipt.cashier_name}</div>
                                <div><strong>การชำระ:</strong> {selectedReceipt.payment_type_name}</div>
                                {selectedReceipt.member_name && selectedReceipt.member_name !== '-' && (
                                    <div style={{ gridColumn: 'span 2' }}>
                                        <strong>สมาชิก:</strong> {selectedReceipt.member_name}
                                    </div>
                                )}
                            </div>

                            {selectedReceipt.items && selectedReceipt.items.length > 0 && (
                                <table className="receipt-items-table">
                                    <thead>
                                        <tr>
                                            <th>รายการ</th>
                                            <th style={{ textAlign: 'center' }}>จำนวน</th>
                                            <th style={{ textAlign: 'right' }}>ราคา</th>
                                            <th style={{ textAlign: 'right' }}>รวม</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedReceipt.items.map((item, idx) => (
                                            <tr key={idx}>
                                                <td>{item.product_name}</td>
                                                <td style={{ textAlign: 'center' }}>{item.quantity}</td>
                                                <td style={{ textAlign: 'right' }}>฿{item.price}</td>
                                                <td style={{ textAlign: 'right' }}>฿{item.total_price}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}

                            <div className="receipt-summary-box">
                                <div className="receipt-summary-row">
                                    <span>ยอดรวมสินค้า:</span>
                                    <span>฿{selectedReceipt.total_price.toLocaleString()}</span>
                                </div>
                                {(selectedReceipt.discount > 0 || selectedReceipt.discount_bill > 0) && (
                                    <div className="receipt-summary-row" style={{ color: '#EF4444' }}>
                                        <span>ส่วนลด:</span>
                                        <span>-฿{(selectedReceipt.discount + selectedReceipt.discount_bill).toLocaleString()}</span>
                                    </div>
                                )}
                                <div className="receipt-summary-row">
                                    <span>ภาษีมูลค่าเพิ่ม (VAT 7%):</span>
                                    <span>฿{selectedReceipt.vat_amount.toLocaleString()}</span>
                                </div>
                                <div className="receipt-summary-row total">
                                    <span>ยอดชำระสุทธิ:</span>
                                    <span>฿{selectedReceipt.net_price.toLocaleString()}</span>
                                </div>
                                <div className="receipt-summary-row" style={{ marginTop: '4px', fontSize: '13px' }}>
                                    <span>รับเงิน: ฿{selectedReceipt.receive.toLocaleString()}</span>
                                    <span>เงินทอน: ฿{selectedReceipt.change.toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default ReportSalesSummary;
